// @owners { team: patients-team }
import { COLORS, SPACING } from '@alto/design-library-tokens';
import { Column, LgSpacing, MdSpacing, XxxlPadding } from '@alto/design-system';
import { AddOnsContext, ProgynyUnboxingGuide, useUnboxingGuide } from '@alto/features';
import React, { useMemo } from 'react';
import styled from 'styled-components';
import { ORIGINS } from '~shared/features/essentials/add_ons_consts';
import { type Prescription, type Shipment } from '~shared/types';
import { FullPage } from '~web/features/essentials/components';
import { DesktopProducts } from '~web/features/essentials/pages/OrderSuccess/DesktopProducts';
import { LastRefillCard } from '~web/features/essentials/pages/OrderSuccess/LastRefillCard';
import { MobileProducts } from '~web/features/essentials/pages/OrderSuccess/MobileProducts';
import { OrderSuccessHeader } from '~web/features/essentials/pages/OrderSuccess/OrderSuccessHeader';
import { useDeprecatedMediaQuery } from '~web/hooks';

const StyledDesktopProductsColumn = styled(Column)`
  overflow: hidden;
`;

type Props = {
  shipment: Shipment;
  isSplitOrder: boolean;
  lastRefillPrescriptions: Prescription[];
  hasCompound: boolean;
};

export const OrderSuccessWithProducts = ({ shipment, isSplitOrder, lastRefillPrescriptions, hasCompound }: Props) => {
  const addOnsContext = useMemo(
    () => ({ shipmentID: shipment?.id, origin: ORIGINS.ORDER_SUCCESS }) as const,
    [shipment?.id],
  );
  const isMobile = !useDeprecatedMediaQuery('SM');
  const showProducts = isSplitOrder || !hasCompound;
  const showDesktopProducts = !isMobile && showProducts;
  const showMobileProducts = isMobile && showProducts;
  const screenPartitions = isMobile || !showDesktopProducts ? '100%' : '50%';
  const { showUnboxingGuide } = useUnboxingGuide({ shipmentID: shipment?.id });

  // Define paddings
  let defaultViewSidePadding:
    | typeof SPACING.STATIC.NONE
    | typeof SPACING.STATIC.LG
    | typeof SPACING.STATIC.XXL
    | undefined;
  if (!showDesktopProducts) {
    defaultViewSidePadding = SPACING.STATIC.NONE;
  } else if (isMobile) {
    defaultViewSidePadding = SPACING.STATIC.LG;
  } else {
    defaultViewSidePadding = SPACING.STATIC.XXL;
  }

  let sidePadding:
    | typeof SPACING.STATIC.NONE
    | typeof SPACING.STATIC.LG
    | typeof SPACING.STATIC.XXL
    | typeof SPACING.STATIC.XXXL
    | undefined;
  if (!showDesktopProducts) {
    sidePadding = SPACING.STATIC.NONE;
  } else {
    // Match the left padding of the left column (64px = XXXL)
    sidePadding = SPACING.STATIC.XXXL;
  }

  return (
    <FullPage>
      <AddOnsContext.Provider value={addOnsContext}>
        <Column
          flexBasis={screenPartitions}
          backgroundColor={COLORS.BACKGROUND_COLORS.TERTIARY_LIGHTEST}
        >
          <XxxlPadding rightPadding={defaultViewSidePadding}>
            <Column centerHorizontally>
              <OrderSuccessHeader
                shipment={shipment}
                isSplitOrder={isSplitOrder}
              />
              <LgSpacing />
              {showUnboxingGuide && (
                <>
                  <LgSpacing />
                  <ProgynyUnboxingGuide />
                </>
              )}
              {lastRefillPrescriptions.length > 0 && <LastRefillCard prescriptions={lastRefillPrescriptions} />}
              {showMobileProducts ? (
                <>
                  <MdSpacing />
                  {shipment ? <MobileProducts /> : null}
                </>
              ) : null}
            </Column>
          </XxxlPadding>
        </Column>
        {showDesktopProducts && (
          <StyledDesktopProductsColumn flexBasis={screenPartitions}>
            <XxxlPadding
              leftPadding={SPACING.STATIC.XXL}
              rightPadding={sidePadding}
            >
              <DesktopProducts />
            </XxxlPadding>
          </StyledDesktopProductsColumn>
        )}
      </AddOnsContext.Provider>
    </FullPage>
  );
};
